/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { CommonModule } from "@angular/common";
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
} from "@angular/core";
import { FormGroup, ReactiveFormsModule } from "@angular/forms";

// PrimeNG imports
import { ButtonModule } from "primeng/button";
import { DividerModule } from "primeng/divider";
import { IconFieldModule } from "primeng/iconfield";
import { InputIconModule } from "primeng/inputicon";
import { InputTextModule } from "primeng/inputtext";
import { MultiSelectModule } from "primeng/multiselect";

// Our interfaces
import {
  IQuickLinksFilterControls,
  QuickLinksFilter,
} from "../../interfaces/quick-links-filter.interface";

/**
 * Quick Links Filters Component
 * Receives filterForm via @Input and uses [formControl] binding
 * Follows reactive forms pattern - NO ngModel!
 */
@Component({
  selector: "app-quick-links-filters",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonModule,
    DividerModule,
    IconFieldModule,
    InputIconModule,
    InputTextModule,
    MultiSelectModule,
  ],
  templateUrl: "./quick-links-filters.component.html",
  styleUrl: "./quick-links-filters.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QuickLinksFiltersComponent {
  @Input() filterForm!: FormGroup<IQuickLinksFilterControls>;
  @Input() loading = false;
  @Input() drawerMode = false;

  @Output() filtersChanged = new EventEmitter<QuickLinksFilter>();
  @Output() clearFilters = new EventEmitter<void>();

  /**
   * Check if there are any active filters
   * Note: Using a method instead of computed signal since we need to check
   * the form's current value reactively
   * Returns true if ANY filter is selected (including status)
   */
  hasActiveFilters(): boolean {
    if (!this.filterForm) return false;

    const values = this.filterForm.value;

    // Return true if ANY filter is selected (including status)
    // This ensures the Clear All button is enabled when filters are visible
    // Note: usageLimitType filter is commented out for now
    return !!(
      values.search?.trim() ||
      values.statuses?.length ||
      values.actionTypes?.length
    );
  }

  // Filter options - using numeric enums to match API
  // Status: 0=Disabled, 1=Enabled, 2=Deleted
  readonly statusOptions = [
    { label: "Enabled", value: 1 },
    { label: "Disabled", value: 0 },
    { label: "Deleted", value: 2 },
  ];

  // ActionType: 0=Resume, 1=Pause, 2=ProcessNow, 3=Reactivate
  readonly actionTypeOptions = [
    { label: "Resume", value: 0 },
    { label: "Pause", value: 1 },
    { label: "Process Now", value: 2 },
    { label: "Reactivate", value: 3 },
  ];

  // UsageLimitType: 0=SingleUse, 1=LimitedUse, 2=Unlimited
  readonly usageLimitOptions = [
    { label: "Single Use", value: 0 },
    { label: "Limited Use", value: 1 },
    { label: "Unlimited", value: 2 },
  ];

  /**
   * Get search section classes based on mode
   */
  getSearchSectionClass(): string {
    return this.drawerMode ? "px-3 pb-3" : "mb-3";
  }

  /**
   * Handle clear filters button click
   */
  onClearFilters(): void {
    this.clearFilters.emit();
  }
}
